import type {
    PermissionRole,
    PoolDetailTab,
    PoolInfoDraft,
    PoolInfoDraftIssues,
    PoolModel,
    PoolPermissionCandidate,
    PoolPermissionHolder,
} from '.';
import { EVENT_EMAIL_PATTERN } from '.';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { PermissionDirective, PermissionUpdateEntry } from '@thinkalpha/common/contracts/permissions.js';
import { isSpecialPermissionSubject, isTypedPermissionDirective } from '@thinkalpha/common/contracts/permissions.js';
import type { UpdatePoolRequest } from '@thinkalpha/locates-ws-client';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { InventoryPool } from '@thinkalpha/ui-types/types/admin/lab-pools';
import { t } from 'i18next';
import { PoolChangeLogModelImpl } from 'src/admin/models/PoolChangeLogModel/impl';
import type { InventoryRowsPerPage } from 'src/admin/models/PoolInventoryModel';
import { PoolInventoryModelImpl } from 'src/admin/models/PoolInventoryModel/impl';
import type { Pool, PoolPermission } from 'src/admin/models/PoolListModel';
import { PoolPricingModelImpl } from 'src/admin/models/PoolPricingModel/impl';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getFirmMembersQuery } from 'src/queries/firms';
import { updatePool, updatePoolPermissionsForEntity } from 'src/queries/labPoolsMutations';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Order roles by privilege so the de-dup step in
 * {@link collectPoolPermissionHolders} keeps the most-privileged tier
 * an entity holds. The numeric values are arbitrary — only the
 * relative ordering matters.
 */
const ROLE_RANK: Record<PermissionRole, number> = {
    reader: 1,
    writer: 2,
    owner: 3,
    admin: 4,
};

/**
 * Extract the user UUIDs (and nothing else) from a single
 * `PermissionDirective` array. The Permissions tab is scoped to firm
 * members; group / firm / special / deny directives all return null
 * so they're filtered out at the top level.
 */
function userIdFromDirective(directive: PermissionDirective): string | null {
    if (isTypedPermissionDirective(directive)) {
        if (directive.mode !== 'allow') return null;
        return directive.type === 'user' ? directive.id : null;
    }
    if (typeof directive === 'string') {
        // Bare-string directives carry no type on the wire. We assume
        // any non-special UUID is a user — special pseudo-subjects
        // (`everyone`, etc.) are intentionally excluded.
        return isSpecialPermissionSubject(directive) ? null : directive;
    }
    return null;
}

/**
 * Walk a pool's `AppliedPermissions` and produce one row per unique
 * user that holds at least one allow directive. Group/firm/special
 * subjects are filtered out — the Permissions tab is firm-members-only.
 * Each row gets the user's highest rung; `userName` and `email` are
 * resolved through the firm-members directory, and users that don't
 * resolve are dropped from the list (UUIDs are never shown).
 */
function collectPoolPermissionHolders(
    pool: InventoryPool | undefined,
    memberLookup: ReadonlyMap<string, { firstName: string; lastName: string; email: string }>,
): ReadonlyArray<PoolPermissionHolder> {
    if (!pool) return [];
    const highestByUser = new Map<string, PermissionRole>();

    const consume = (directives: PermissionDirective[], role: PermissionRole) => {
        for (const directive of directives) {
            const userId = userIdFromDirective(directive);
            if (!userId) continue;
            const existing = highestByUser.get(userId);
            if (existing && ROLE_RANK[existing] >= ROLE_RANK[role]) continue;
            highestByUser.set(userId, role);
        }
    };
    consume(pool.permissions.admins, 'admin');
    consume(pool.permissions.owners, 'owner');
    consume(pool.permissions.writers, 'writer');
    consume(pool.permissions.readers, 'reader');

    const out: PoolPermissionHolder[] = [];
    for (const [userId, role] of highestByUser) {
        const profile = memberLookup.get(userId);
        if (!profile) continue;
        const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
        out.push({ userId, userName, email: profile.email, role });
    }
    return out;
}

const EMPTY_POOL_INFO: PoolInfoDraft = { name: '', description: '', eventEmail: '' };

function poolInfoFrom(pool: Pool | null): PoolInfoDraft {
    if (!pool) return EMPTY_POOL_INFO;
    return { name: pool.name, description: pool.description ?? '', eventEmail: pool.eventEmail };
}

function isSamePoolInfo(a: PoolInfoDraft, b: PoolInfoDraft): boolean {
    return a.name === b.name && a.description === b.description && a.eventEmail === b.eventEmail;
}

/** Everything the pool model needs from its owner (the list side). */
export interface PoolModelDeps {
    /** The pool this model represents; part of the instance's identity. */
    readonly poolId: string;
    readonly queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly queryClient: ReactBindings['QueryClient'];
    readonly store: ReactBindings['Store'];
    /** The pool's payload resolved from the owner's list; null until it loads. */
    readonly pool: ReadonlySignal<Pool | null>;
    /**
     * The raw API pool (with applied permissions) from the owner's list
     * query — the Permissions tab's holder list reads the permission
     * directives, which the UI-facing {@link Pool} deliberately omits.
     */
    readonly rawPool: ReadonlySignal<InventoryPool | undefined>;
    /** Legacy-pool gate per the `legacy-lab-pools` feature flag. */
    readonly isLegacyPool: ReadonlySignal<boolean>;
    /** Session-level superuser access; gates the Permissions tab. */
    readonly hasSuperuserSession: ReadonlySignal<boolean>;
    /** Active user's roles, for the pricing model's method gates. */
    readonly userRoles: ReactBindings['ActiveUserModel']['userRoles'];
    /** Owner-held inventory page-size preference (sticks across pools). */
    readonly inventoryRowsPerPage: Signal<InventoryRowsPerPage>;
    /** Owner-held change-log page-size preference (sticks across pools). */
    readonly changeLogRowsPerPage: Signal<InventoryRowsPerPage>;
    /**
     * A mutation changed pool-level data — the owner invalidates its
     * pool list and bumps its freshness stamp.
     */
    onPoolsChanged(): Promise<void>;
    /** A child refetched its server data — the owner bumps its freshness stamp. */
    onFresh(): void;
}

/** See {@link PoolModel}. */
export class PoolModelImpl implements PoolModel {
    readonly #disposableStack = new DisposableStack();
    readonly #deps: PoolModelDeps;

    readonly poolId: string;
    readonly pool: ReadonlySignal<Pool | null>;

    readonly inventoryModel: PoolInventoryModelImpl;
    readonly changeLogModel: PoolChangeLogModelImpl;
    readonly pricingModel: PoolPricingModelImpl;

    /**
     * Firm members of the pool's firm — only fetched once the
     * Permissions tab is active, to keep the directory request lazy.
     * The Permissions tab uses this to resolve user UUIDs to names and
     * to populate the grant-access selector.
     */
    readonly #firmMembersQuery: QueryObserverModel<
        ReadonlyArray<FirmMembershipWithProfile>,
        Error,
        ReadonlyArray<FirmMembershipWithProfile>
    >;

    constructor(deps: PoolModelDeps) {
        this.#deps = deps;
        this.poolId = deps.poolId;
        this.pool = deps.pool;

        this.inventoryModel = new PoolInventoryModelImpl({
            poolId: deps.poolId,
            queryObserverModelFactory: deps.queryObserverModelFactory,
            queryClient: deps.queryClient,
            store: deps.store,
            canWrite: this.canWrite,
            isLegacyPool: deps.isLegacyPool,
            poolName: computed(() => deps.pool.value?.name ?? null),
            rowsPerPage: deps.inventoryRowsPerPage,
            onServerRowsRefetched: () => deps.onFresh(),
        });
        // Children join the disposable stack (LIFO: reverse construction
        // order) so one throwing dispose can't strand the others. `defer`
        // rather than `use` so the dispose method is looked up at teardown
        // (tests observe disposal by spying on the instance).
        this.#disposableStack.defer(() => this.inventoryModel[Symbol.dispose]());

        this.changeLogModel = new PoolChangeLogModelImpl({
            poolId: deps.poolId,
            queryObserverModelFactory: deps.queryObserverModelFactory,
            queryClient: deps.queryClient,
            store: deps.store,
            // Only fetch while the user is actually viewing the tab — the
            // audit log is large enough that it shouldn't refresh eagerly
            // alongside the inventory grid.
            isActive: computed(() => this.#activeTab.value === 'change-log'),
            isLegacyPool: deps.isLegacyPool,
            poolName: computed(() => deps.pool.value?.name ?? null),
            rowsPerPage: deps.changeLogRowsPerPage,
        });
        this.#disposableStack.defer(() => this.changeLogModel[Symbol.dispose]());

        this.pricingModel = new PoolPricingModelImpl({
            poolId: deps.poolId,
            queryClient: deps.queryClient,
            store: deps.store,
            pool: deps.pool,
            canRead: this.canRead,
            canOwn: this.canOwn,
            hasSuperuserSession: deps.hasSuperuserSession,
            userRoles: deps.userRoles,
            inventoryRows: this.inventoryModel.displayedRows,
            pendingDraftRows: this.inventoryModel.pendingDraftRows,
            onConfigSaved: () => deps.onPoolsChanged(),
        });
        this.#disposableStack.defer(() => this.pricingModel[Symbol.dispose]());

        const firmMembersOptions = computed<
            QueryObserverOptions<
                ReadonlyArray<FirmMembershipWithProfile>,
                Error,
                ReadonlyArray<FirmMembershipWithProfile>
            >
        >(() => {
            // The members directory is only needed by the Permissions tab.
            // Disable the query when the tab isn't open; the firm-id key
            // means the cache is shared with any other admin surface that
            // already fetched it. Gate on the tab being actually
            // accessible: non-superusers can store `permissions` but never
            // see the tab, so the fetch would be wasted.
            const showsPermissions = this.#activeTab.value === 'permissions' && this.canManagePermissions.value;
            const firmId = showsPermissions ? deps.pool.value?.firmId : undefined;
            return getFirmMembersQuery(firmId);
        });
        this.#firmMembersQuery = deps.queryObserverModelFactory<
            ReadonlyArray<FirmMembershipWithProfile>,
            Error,
            ReadonlyArray<FirmMembershipWithProfile>
        >(firmMembersOptions);
        this.#disposableStack.use(this.#firmMembersQuery);

        // When `canManagePermissions` flips off (superuser session
        // expires or is toggled off externally) any in-flight permission-
        // change confirmation must be dropped — the dialog would
        // otherwise sit open with a callback that re-enters
        // `#putUserPermission` even though the tab is now hidden.
        this.#disposableStack.defer(
            effect(() => {
                if (!this.canManagePermissions.value) {
                    this.#pendingPermissionChange.value = null;
                }
            }),
        );

        // The info baseline seeds from the pool payload and reseats when
        // the server copy changes under a clean draft (PLAT-12970). The
        // last SERVER copy is tracked separately — comparing against
        // `#poolInfoSaved` would re-fire when a submit echo moves the
        // baseline and clobber it back to the stale cache copy.
        let lastServerInfo: PoolInfoDraft | null = null;
        this.#disposableStack.defer(
            effect(() => {
                const pool = deps.pool.value;
                // While the payload is null (deep link before the list
                // resolves) the empty placeholder seeded at construction
                // stands; the first loaded copy seeds for real.
                if (pool === null) return;
                const serverInfo = poolInfoFrom(pool);
                if (lastServerInfo !== null && isSamePoolInfo(serverInfo, lastServerInfo)) return;
                lastServerInfo = serverInfo;
                // `peek` so baseline/draft writes elsewhere don't re-run
                // the effect; edits made while a PATCH was in flight stay
                // dirty against the new baseline.
                const infoWasClean = isSamePoolInfo(this.#poolInfoDraft.peek(), this.#poolInfoSaved.peek());
                this.#poolInfoSaved.value = serverInfo;
                if (infoWasClean) this.#poolInfoDraft.value = serverInfo;
            }),
        );
    }

    readonly permission: ReadonlySignal<PoolPermission | null> = computed(
        () => this.#deps.pool.value?.permission ?? null,
    );
    readonly isLegacy: ReadonlySignal<boolean> = computed(() => this.#deps.isLegacyPool.value);

    // --- Access gates ---
    readonly canRead: ReadonlySignal<boolean> = computed(() => this.permission.value !== null);
    readonly canWrite: ReadonlySignal<boolean> = computed(() => {
        const p = this.permission.value;
        return p === 'writer' || p === 'owner';
    });
    readonly canOwn: ReadonlySignal<boolean> = computed(() => this.permission.value === 'owner');
    /** Access gate for the Permissions tab and its mutations. */
    readonly canManagePermissions: ReadonlySignal<boolean> = computed(() => this.#deps.hasSuperuserSession.value);

    // --- Detail tabs ---
    /** Raw stored tab; every fresh instance lands on the Info tab. */
    readonly #activeTab = signal<PoolDetailTab>('info');
    readonly activePoolDetailTab: ReadonlySignal<PoolDetailTab> = computed(() => {
        const stored = this.#activeTab.value;
        // Non-superusers can't see the Permissions tab — coerce a stored
        // `permissions` value on read so they land on the Info tab.
        if (stored === 'permissions' && !this.canManagePermissions.value) return 'info';
        return stored;
    });
    setActivePoolDetailTab(tab: PoolDetailTab): void {
        // Hard-stop attempts to land on Permissions when the user can't
        // see it; this prevents the view from briefly opening the tab on
        // a stale dispatch.
        if (tab === 'permissions' && !this.canManagePermissions.value) return;
        this.#activeTab.value = tab;
    }

    // --- Unsaved-work aggregates ---
    /**
     * Anything the discard prompts must protect: staged inventory work,
     * an unsaved pricing config draft, or an unsaved pool info draft.
     * Disposal on navigation discards all three implicitly.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean> = computed(
        () => this.inventoryModel.isDirty.value || this.pricingModel.isDirty.value || this.isPoolInfoDirty.value,
    );
    readonly showSubmitChangesBar: ReadonlySignal<boolean> = computed(() => {
        if (!this.canWrite.value) return false;
        const inventory = this.inventoryModel;
        return inventory.isDirty.value || inventory.isSubmitting.value || inventory.submitError.value !== null;
    });

    // --- Pool info editing (PLAT-12970) ---
    /**
     * The pool PATCH requires `owner`. Deliberately not legacy-gated:
     * legacy pools are gaining PATCH support (name/description/email),
     * and until it lands a failed PATCH surfaces as an error toast.
     */
    readonly canEditPoolInfo: ReadonlySignal<boolean> = computed(() => this.canOwn.value);

    readonly #poolInfoSaved = signal<PoolInfoDraft>(EMPTY_POOL_INFO);
    readonly #poolInfoDraft = signal<PoolInfoDraft>(EMPTY_POOL_INFO);
    readonly #isSavingPoolInfo = signal(false);

    readonly poolInfoSaved: ReadonlySignal<PoolInfoDraft> = computed(() => this.#poolInfoSaved.value);
    readonly poolInfoDraft: ReadonlySignal<PoolInfoDraft> = computed(() => this.#poolInfoDraft.value);
    readonly isPoolInfoDirty: ReadonlySignal<boolean> = computed(
        () => !isSamePoolInfo(this.#poolInfoDraft.value, this.#poolInfoSaved.value),
    );
    readonly poolInfoDraftIssues: ReadonlySignal<PoolInfoDraftIssues> = computed(() => {
        const draft = this.#poolInfoDraft.value;
        return {
            nameBlank: draft.name.trim() === '',
            emailMalformed: !EVENT_EMAIL_PATTERN.test(draft.eventEmail.trim()),
        };
    });
    readonly isPoolInfoDraftValid: ReadonlySignal<boolean> = computed(() => {
        const issues = this.poolInfoDraftIssues.value;
        return !issues.nameBlank && !issues.emailMalformed;
    });
    readonly isSavingPoolInfo: ReadonlySignal<boolean> = computed(() => this.#isSavingPoolInfo.value);

    setPoolInfoField(field: keyof PoolInfoDraft, value: string): void {
        if (!this.canEditPoolInfo.value) return;
        this.#poolInfoDraft.value = { ...this.#poolInfoDraft.value, [field]: value };
    }

    discardPoolInfoChanges(): void {
        if (!this.canEditPoolInfo.value) return;
        // A discard during an in-flight PATCH would be indistinguishable
        // from a mid-flight edit at settle time, resurrecting the old
        // value as dirty against the new baseline — so it waits.
        if (this.#isSavingPoolInfo.value) return;
        this.#poolInfoDraft.value = this.#poolInfoSaved.value;
    }

    submitPoolInfo(): void {
        if (!this.canEditPoolInfo.value) return;
        if (this.#isSavingPoolInfo.value) return;
        if (!this.isPoolInfoDirty.value || !this.isPoolInfoDraftValid.value) return;
        const poolId = this.poolId;
        const saved = this.#poolInfoSaved.value;
        const submitted = this.#poolInfoDraft.value;
        // Send only the fields that changed; the backend leaves absent
        // fields untouched.
        const request = {
            ...(submitted.name !== saved.name ? { name: submitted.name.trim() } : {}),
            ...(submitted.description !== saved.description ? { description: submitted.description.trim() } : {}),
            ...(submitted.eventEmail !== saved.eventEmail ? { eventEmail: submitted.eventEmail.trim() } : {}),
        } satisfies UpdatePoolRequest;
        this.#isSavingPoolInfo.value = true;
        void updatePool({ poolId, request }).then(
            async (response) => {
                this.#isSavingPoolInfo.value = false;
                // Instance-scoped: a pool switch disposes this model, so
                // the echo can never land on another pool's draft. Writes
                // after disposal are inert (nothing reads the signals).
                const echoed: PoolInfoDraft = {
                    name: response.pool.name,
                    description: response.pool.description ?? '',
                    eventEmail: response.pool.eventEmail,
                };
                // Reconcile against the server's echo (it trims), but
                // never clobber edits made while the PATCH was in
                // flight; those stay dirty against the new baseline.
                const untouched = isSamePoolInfo(this.#poolInfoDraft.value, submitted);
                this.#poolInfoSaved.value = echoed;
                if (untouched) this.#poolInfoDraft.value = echoed;
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-info-saved'),
                        group: 'default',
                        variant: 'success',
                    },
                });
                try {
                    await this.#deps.onPoolsChanged();
                } catch {
                    // Best-effort cache refresh — a failed invalidation just
                    // leaves the stale pool entry until the next fetch.
                }
            },
            (error: unknown) => {
                this.#isSavingPoolInfo.value = false;
                const message = error instanceof Error ? error.message : String(error);
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-info-save-failed', { message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    // --- Pool permissions ---
    /**
     * `userId -> { firstName, lastName, email }` derived from the
     * firm-members query. Used to resolve the holder list and to
     * power the grant-access selector.
     */
    readonly #memberLookup: ReadonlySignal<
        ReadonlyMap<string, { firstName: string; lastName: string; email: string }>
    > = computed(() => {
        const members = this.#firmMembersQuery.signal.value.data ?? [];
        const map = new Map<string, { firstName: string; lastName: string; email: string }>();
        for (const m of members) {
            const profile = m as unknown as {
                userId?: string;
                profile?: { firstName: string; lastName: string; email: string };
            };
            const userId = profile.userId;
            if (!userId || !profile.profile) continue;
            map.set(userId, profile.profile);
        }
        return map;
    });

    /**
     * Stages a role change for the confirm dialog. Instance-scoped, so
     * unlike the old facade it doesn't need to capture a pool id — a
     * pool switch disposes the model (and the staged change with it).
     */
    readonly #pendingPermissionChange = signal<{ userId: string; role: PermissionRole } | null>(null);

    /**
     * In-flight permission mutations counter so the view can disable
     * controls while a PUT is settling. A counter (not a boolean)
     * because we may fan out multiple calls from a single user action
     * (e.g. revoke all tiers for a holder).
     */
    readonly #permissionsMutationsInFlight = signal<number>(0);

    readonly poolPermissionHolders: ReadonlySignal<ReadonlyArray<PoolPermissionHolder>> = computed(() =>
        collectPoolPermissionHolders(this.#deps.rawPool.value, this.#memberLookup.value),
    );

    readonly poolPermissionCandidates: ReadonlySignal<ReadonlyArray<PoolPermissionCandidate>> = computed(() => {
        const holders = this.poolPermissionHolders.value;
        const taken = new Set(holders.map((h) => h.userId));
        const out: PoolPermissionCandidate[] = [];
        for (const [userId, profile] of this.#memberLookup.value) {
            if (taken.has(userId)) continue;
            const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
            out.push({ userId, userName, email: profile.email });
        }
        // Stable alphabetical sort so the dropdown order doesn't churn
        // when the underlying map iterates differently after a refetch.
        out.sort((a, b) => a.userName.localeCompare(b.userName));
        return out;
    });

    readonly pendingPermissionChange: ReadonlySignal<{
        userId: string;
        userName: string;
        role: PermissionRole;
    } | null> = computed(() => {
        const change = this.#pendingPermissionChange.value;
        if (!change) return null;
        const profile = this.#memberLookup.value.get(change.userId);
        const userName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : change.userId;
        return { userId: change.userId, userName, role: change.role };
    });

    readonly permissionsMutating: ReadonlySignal<boolean> = computed(
        () => this.#permissionsMutationsInFlight.value > 0,
    );

    requestSetPoolPermissionRole(userId: string, role: PermissionRole): void {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        this.#pendingPermissionChange.value = { userId, role };
    }

    cancelSetPoolPermissionRole(): void {
        this.#pendingPermissionChange.value = null;
    }

    async confirmSetPoolPermissionRole(): Promise<void> {
        const change = this.#pendingPermissionChange.value;
        if (!change) return;
        // Fail closed if either gate has been revoked since the dialog
        // opened: drop the pending change instead of firing a PUT the
        // caller is no longer allowed to make.
        if (!this.canManagePermissions.value || !this.canOwn.value) {
            this.#pendingPermissionChange.value = null;
            return;
        }
        this.#pendingPermissionChange.value = null;
        await this.#putUserPermission(change.userId, [{ access: change.role, kind: 'allow' }]);
    }

    async addPoolPermission(userId: string, role: PermissionRole): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        await this.#putUserPermission(userId, [{ access: role, kind: 'allow' }]);
    }

    async removePoolPermission(userId: string): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        // Empty entries array clears every rung the user holds.
        await this.#putUserPermission(userId, []);
    }

    /**
     * Issue the PUT against the `user` entity-type endpoint, increment /
     * decrement the in-flight counter so the view can disable controls,
     * dispatch success / error toasts, and have the owner invalidate the
     * pool list so the table refreshes with the server's new
     * `permissions` payload.
     */
    async #putUserPermission(userId: string, entries: PermissionUpdateEntry[]): Promise<void> {
        const profile = this.#memberLookup.value.get(userId);
        const displayName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : userId;
        this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value + 1;
        try {
            try {
                await updatePoolPermissionsForEntity({
                    poolId: this.poolId,
                    entityType: 'user',
                    entityId: userId,
                    entries,
                });
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-permissions-error', {
                            name: displayName,
                            message,
                        }),
                        group: 'default',
                        variant: 'error',
                    },
                });
                return;
            }
            // The PUT succeeded; a failed cache refresh must not surface as
            // a failed permission change (mirrors submitPoolInfo's
            // best-effort invalidation).
            try {
                await this.#deps.onPoolsChanged();
            } catch {
                // Best-effort — the next refetch reconciles.
            }
            const messageKey =
                entries.length === 0
                    ? 'admin::pool-management::toast-permissions-removed'
                    : 'admin::pool-management::toast-permissions-updated';
            this.#deps.store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(messageKey, { name: displayName }),
                    group: 'default',
                    variant: 'success',
                },
            });
        } finally {
            this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value - 1;
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
